import { DotLottieReact, type DotLottie } from '@lottiefiles/dotlottie-react';
import type { AlertColor } from '@mui/material/Alert';
import { ReactNode, useCallback, useState } from 'react';
import { usePrefersReducedMotion } from '../../hooks/use-prefers-reduced-motion';
import { Button } from '../button';
import { MuiAlert, MuiAlertTitle } from '../mui-alert';
import { Stack } from '../stack';
import { Typography } from '../typography';
import { EmptyStateSize, IMAGE_MAP, isLottieFile, type EmptyStateImage } from './utils';

type EmptyStateWarning = {
	/**
	 * The title displayed in the alert header.
	 */
	title?: string;
	/**
	 * The body text of the alert.
	 */
	description?: string;
	/**
	 * The severity level of the alert.
	 */
	severity?: AlertColor;
};

const CONTAINER_WIDTH: Record<EmptyStateSize, string> = {
	[EmptyStateSize.XLarge]: '600px',
	[EmptyStateSize.Large]: '330px',
	[EmptyStateSize.Medium]: '280px',
	[EmptyStateSize.Small]: '110px'
};

const IMAGE_MAX_WIDTH: Record<EmptyStateSize, string> = {
	[EmptyStateSize.XLarge]: '300px',
	[EmptyStateSize.Large]: '300px',
	[EmptyStateSize.Medium]: '180px',
	[EmptyStateSize.Small]: '96px'
};

const CONTAINER_PADDING: Record<EmptyStateSize, string> = {
	[EmptyStateSize.XLarge]: '16px',
	[EmptyStateSize.Large]: '16px',
	[EmptyStateSize.Medium]: '16px',
	[EmptyStateSize.Small]: '8px'
};

const SECTION_GAP: Record<EmptyStateSize, string> = {
	[EmptyStateSize.XLarge]: '24px',
	[EmptyStateSize.Large]: '24px',
	[EmptyStateSize.Medium]: '16px',
	[EmptyStateSize.Small]: '16px'
};

const HEADING_PADDING_BOTTOM: Record<EmptyStateSize, string> = {
	[EmptyStateSize.XLarge]: '12px',
	[EmptyStateSize.Large]: '12px',
	[EmptyStateSize.Medium]: '8px',
	[EmptyStateSize.Small]: '7px'
};

const HEADING_VARIANT = {
	[EmptyStateSize.XLarge]: 'h4',
	[EmptyStateSize.Large]: 'h4',
	[EmptyStateSize.Medium]: 'h5',
	[EmptyStateSize.Small]: 'body1'
} as const;

const imageStyle = (size: EmptyStateSize) => ({
	alignSelf: 'center',
	aspectRatio: '3 / 2',
	maxWidth: IMAGE_MAX_WIDTH[size],
	overflow: 'hidden',
	width: '100%'
});

const getOuterWidth = (size: EmptyStateSize, textMaxWidth?: string) => {
	if (size === EmptyStateSize.XLarge) return '100%';
	if (textMaxWidth) return `max(${IMAGE_MAX_WIDTH[size]}, ${textMaxWidth})`;
	return CONTAINER_WIDTH[size];
};

export type EmptyStateProps = {
	/**
	 * Controls the container, artwork, spacing, and heading treatment.
	 * @default 'large'
	 */
	size?: EmptyStateSize;
	/**
	 * The main heading of the empty state. Accepts a string (rendered using the size's Typography variant)
	 * or a custom ReactNode for full styling control.
	 */
	title: ReactNode;
	/**
	 * The descriptive body text shown below the title. Accepts a string (rendered as body1
	 * Typography) or a custom ReactNode for full styling control.
	 */
	description?: ReactNode;
	/**
	 * The name of the SVG image to display above the title.
	 */
	image?: EmptyStateImage;
	/**
	 * Optional warning / info alert shown below the description.
	 */
	warning?: EmptyStateWarning;
	/**
	 * URL opened in a new tab when the link button is clicked.
	 */
	link?: string;
	/**
	 * Label for the optional external link button.
	 */
	linkButtonTitle?: string;
	/**
	 * Additional action buttons rendered alongside the link button.
	 */
	extraButtons?: ReactNode;
	/**
	 * Optional caption text shown below the description (rendered as body2 typography).
	 */
	captionText?: string;
	/**
	 * Overrides the max-width of the text content area, allowing text to exceed the image
	 * width. Useful with smaller image sizes (e.g. Small) where the default text width
	 * would otherwise be too narrow.
	 */
	textMaxWidth?: string;
};

export const EmptyState = ({
	size = EmptyStateSize.Large,
	title,
	description,
	image,
	warning,
	link,
	linkButtonTitle,
	extraButtons,
	captionText,
	textMaxWidth
}: EmptyStateProps) => {
	const [dotLottie, setDotLottie] = useState<DotLottie | null>(null);
	const prefersReducedMotion = usePrefersReducedMotion();

	const handleMouseEnter = () => {
		if (prefersReducedMotion) return;
		dotLottie?.setFrame(0);
		dotLottie?.play();
	};

	const dotLottieRefCallback = useCallback(
		(instance: DotLottie | null) => {
			setDotLottie(instance);
			if (instance && prefersReducedMotion) {
				const showLastFrame = () => instance.setFrame(instance.totalFrames - 1);
				if (instance.isLoaded) {
					showLastFrame();
				} else {
					instance.addEventListener('load', showLastFrame);
				}
			}
		},
		[prefersReducedMotion]
	);

	const resolvedTextMaxWidth = textMaxWidth ?? '100%';

	return (
		<Stack
			direction="column"
			justifyContent="center"
			alignItems="center"
			style={{
				boxSizing: 'border-box',
				gap: SECTION_GAP[size],
				height: '100%',
				marginLeft: 'auto',
				marginRight: 'auto',
				maxWidth: size === EmptyStateSize.XLarge ? CONTAINER_WIDTH[size] : '100%',
				overflow: 'hidden',
				padding: CONTAINER_PADDING[size],
				width: getOuterWidth(size, textMaxWidth)
			}}
		>
			{image ? (
				isLottieFile(IMAGE_MAP[image]) ? (
					<div style={{ ...imageStyle(size), cursor: 'pointer' }} onMouseEnter={handleMouseEnter}>
						<DotLottieReact src={IMAGE_MAP[image]} autoplay={!prefersReducedMotion} dotLottieRefCallback={dotLottieRefCallback} />
					</div>
				) : (
					<img src={IMAGE_MAP[image]} alt={typeof title === 'string' ? title : ''} style={{ ...imageStyle(size), objectFit: 'contain' }} />
				)
			) : null}
			<Stack direction="column" alignItems="center" gap={0} style={{ maxWidth: resolvedTextMaxWidth, width: '100%' }}>
				<Stack alignItems="center" pb={HEADING_PADDING_BOTTOM[size]} width="100%">
					{typeof title === 'string' ? (
						<Typography
							variant={HEADING_VARIANT[size]}
							sx={{
								fontWeight: size === EmptyStateSize.Small ? 'bold' : undefined,
								textAlign: 'center',
								whiteSpace: size === EmptyStateSize.Small ? 'normal' : 'nowrap'
							}}
						>
							{title}
						</Typography>
					) : (
						title
					)}
				</Stack>
				{typeof description === 'string' ? (
					<Typography variant="body1" style={{ textAlign: 'center', width: '100%' }}>
						{description}
					</Typography>
				) : (
					description
				)}
				{captionText ? (
					<Typography variant="body2" style={{ marginTop: '0.5rem', textAlign: 'center' }}>
						{captionText}
					</Typography>
				) : null}
				{warning ? (
					<MuiAlert severity={warning.severity ?? 'info'} sx={{ mt: 2 }}>
						{warning.title ? <MuiAlertTitle>{warning.title}</MuiAlertTitle> : null}
						{warning.description ?? null}
					</MuiAlert>
				) : null}
			</Stack>
			{(link ?? extraButtons) ? (
				<Stack direction="row" justifyContent="center" alignItems="center" gap={2}>
					{link ? (
						<Button
							endIconName="OpenInNew"
							size="medium"
							onClick={() => window.open(link, '_blank', 'noopener,noreferrer')}
							sx={{ whiteSpace: 'nowrap' }}
						>
							{linkButtonTitle ?? 'Learn more'}
						</Button>
					) : null}
					{extraButtons ?? null}
				</Stack>
			) : null}
		</Stack>
	);
};
